/**
 * The quality score as a ring.
 *
 * Two things it does that the old 64px gauge did not:
 *
 *   1. It shows the score `quality_score_basis` NAMES on the outer arc, and —
 *      when that is the AI-adjusted number — draws the deterministic score as a
 *      faint ghost arc behind it. A bare 0.72 hides that it began as 0.90 and
 *      was moved by a reviewer, which is the very thing a reader needs in order
 *      to judge it.
 *   2. It is coloured by the SCORE BAND, not by `status`. A chain can raise no
 *      findings at all and still score 0.45, because the score is mostly
 *      coverage — a green ring on that number would say the opposite of what
 *      the number says. The status badge sits beside the ring and carries the
 *      findings verdict separately.
 *
 * The band word is always rendered: the ring must never be colour alone.
 */

import Tooltip from '../../ui/Tooltip';
import type { ValidationSummary } from '../../../types/dataQuality';
import { formatQualityScore, resolveScore } from '../utils/dataQualityFormat';
import { scoreBand } from './dqTokens';

export interface QualityRingProps {
  summary: ValidationSummary;
  size?: number;
  /** Hidden on dense surfaces where a caption already names the score. */
  showBand?: boolean;
}

export default function QualityRing({
  summary,
  size = 112,
  showBand = true,
}: QualityRingProps) {
  const { score, isAgentAdjusted } = resolveScore(summary);
  if (score == null || !Number.isFinite(score)) return null;

  const band = scoreBand(score);
  const stroke = size >= 96 ? 8 : 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, score));

  // The deterministic score, drawn behind the authoritative one so the reader
  // can see the distance the review moved it. Only when the two differ.
  const ruleScore = summary.quality_score;
  const showGhost =
    isAgentAdjusted &&
    Number.isFinite(ruleScore) &&
    Math.abs(ruleScore - score) > 0.005;
  const ghostClamped = Math.max(0, Math.min(1, ruleScore));

  const tooltip = showGhost
    ? `${formatQualityScore(score)} after AI review, from a rule score of ${formatQualityScore(ruleScore)}.`
    : `Quality score ${formatQualityScore(score)} — ${band.label.toLowerCase()}.`;

  return (
    <div className="flex shrink-0 flex-col items-center gap-1.5">
      <Tooltip content={tooltip} position="left">
        <div className="relative" style={{ width: size, height: size }}>
          {/* Rotated so the arc starts at twelve o'clock. */}
          <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#ecebe8" // neutral-200 / brand cream
              strokeWidth={stroke}
            />
            {showGhost && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={band.hex}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - ghostClamped)}
                opacity={0.25}
              />
            )}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={band.hex}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - clamped)}
              // Honour a reader who has asked for less movement.
              className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="font-mono font-semibold tabular-nums text-neutral-900"
              style={{ fontSize: Math.round(size / 4.2) }}
            >
              {formatQualityScore(score)}
            </span>
            {size >= 96 && (
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                {isAgentAdjusted ? 'reviewed' : 'score'}
              </span>
            )}
          </div>
        </div>
      </Tooltip>

      {/* The word, so the ring is never colour alone. */}
      {showBand && (
        <span className={`text-xs font-semibold ${band.text}`}>{band.label}</span>
      )}
    </div>
  );
}
